/* global gsap, ScrollTrigger */

/**
 * Sticky Cart Component for Horizon Theme
 * Adapted from Trade theme implementation with Horizon component system integration
 */

// Import Horizon theme utilities and events
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Sticky Cart Component
 * Manages scroll-based visibility and variant selection for sticky add-to-cart functionality
 */
class StickyCartComponent extends HTMLElement {
  constructor() {
    super();
    
    /**
     * Width of window when sticky cart was last hidden
     */
    this.lastHiddenWidth = null;

    /**
     * Scroll triggers for showing/hiding sticky cart
     */
    this.scrollTriggers = {};

    /**
     * Current selected variant ID
     */
    this.currentVariantId = null;

    /**
     * Product information container element
     */
    this.productInfoContainer = null;

    /**
     * Footer element for hiding sticky cart
     */
    this.footer = null;

    /**
     * Native add to cart button for synchronization
     */
    this.nativeAddToCartButton = null;

    /**
     * Last known trigger states
     */
    this.triggerStates = {
      mainTriggerActive: true,
      footerActive: false
    };

    /**
     * Indicates if the main add-to-cart element has scrolled past the top of the viewport
     */
    this.mainElementScrolledPast = false;

    /**
     * Configuration for scroll triggers
     */
    this.config = {
      mainTriggerOffset: '150%',
      mainTriggerEndOffset: '50%',
      footerOffset: '50%',
      footerPixelOffset: 0
    };

    /**
     * Mobile panel state and elements
     */
    this.mobilePanel = {
      isOpen: false,
      focusableElements: null,
      firstFocusableElement: null,
      lastFocusableElement: null,
      isToggling: false
    };

    /**
     * Original button content for restoration
     */
    this.originalButtonContent = null;
  }

  /**
   * Initialize the sticky cart component
   */
  connectedCallback() {
    // Find required elements
    this.findElements();
    
    // Initialize scroll triggers if GSAP is available
    if (typeof gsap !== 'undefined' && gsap.registerPlugin) {
      this.initializeScrollTriggers();
    }
    
    // Set up event listeners
    this.addEventListeners();
    
    // Initialize variant state
    this.updateSelectedVariantFromDOM();
    
    // Set up resize handling
    this.setupResizeHandling();
  }

  /**
   * Cleanup when component is disconnected
   */
  disconnectedCallback() {
    // Clean up scroll triggers
    if (this.triggerInstances) {
      if (this.triggerInstances.main && this.triggerInstances.main.kill) {
        this.triggerInstances.main.kill();
        this.triggerInstances.main = null;
      }
      
      if (this.triggerInstances.footer && this.triggerInstances.footer.kill) {
        this.triggerInstances.footer.kill();
        this.triggerInstances.footer = null;
      }
    }
    
    // Clean up IntersectionObservers
    if (this.mainObserver) {
      this.mainObserver.disconnect();
      this.mainObserver = null;
    }
    
    if (this.footerObserver) {
      this.footerObserver.disconnect();
      this.footerObserver = null;
    }
    
    // Remove scroll event listeners
    if (this.checkTriggerLinePassed) {
      window.removeEventListener('scroll', this.checkTriggerLinePassed);
      this.checkTriggerLinePassed = null;
    }
    
    // Legacy cleanup for backward compatibility
    if (this.scrollTriggers) {
      Object.values(this.scrollTriggers).forEach(trigger => {
        if (trigger && trigger.kill) trigger.kill();
      });
    }
    
    // Remove event listeners
    this.removeEventListeners();
  }

  /**
   * Find and cache DOM elements
   */
  findElements() {
    // Find product info container - try multiple Horizon theme selectors
    this.productInfoContainer = document.querySelector(
      '.product__info-container, [data-product-info], .product-information, .product-details, .product__details, .product-form-container'
    );
    
    // Find native add to cart button - try multiple Horizon theme selectors
    this.nativeAddToCartButton = document.querySelector(
      'product-form-component [ref="addToCartButton"], [data-cart-form] button[type="submit"], .product-form__submit button, add-to-cart-component button, .product-form button[type="submit"]'
    );
    
    // Find footer element
    const footerSelector = '.shopify-section.shopify-section-group-footer-group, .shopify-section-group-footer-group, footer, .footer, [data-section-type="footer"], .site-footer';
    const potentialFooters = document.querySelectorAll(footerSelector);
    this.footer = potentialFooters.length > 0 ? potentialFooters[0] : null;

    // Cache refs for easier access
    this.stickyContainer = this;
    this.variantButtons = this.querySelectorAll('[data-variant-button]');
    this.addToCartButton = this.querySelector('[data-sticky-add]');
    this.mobileVariantTrigger = this.querySelector('[data-mobile-variant-trigger]');
    this.mobileVariantPanel = this.querySelector('[data-variant-panel]');
    this.priceElements = this.querySelectorAll('[data-price]');
    this.productImage = this.querySelector('[data-sticky-product-image]');
    
    // Store original button text for error recovery
    if (this.addToCartButton && !this.addToCartButton.getAttribute('data-original-text')) {
      this.addToCartButton.setAttribute('data-original-text', this.addToCartButton.textContent?.trim() || '');
    }
  }

  /**
   * Initialize scroll triggers using GSAP ScrollTrigger
   */
  initializeScrollTriggers() {
    // Ensure GSAP and ScrollTrigger are available
    if (typeof gsap === 'undefined' || !gsap.registerPlugin) {
      console.error('GSAP or ScrollTrigger not available for StickyCartComponent.');
      return;
    }
    if (!gsap.plugins.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    const mainTriggerElement = this.nativeAddToCartButton || this.productInfoContainer;
    
    if (!mainTriggerElement) {
      return;
    }

    this.triggerInstances = {
      main: null,
      footer: null,
      creationCount: 0
    };
    
    let triggerOffsetValue = 0;
    /** @type {HTMLElement | null} */
    const stickyCartWrapper = this.closest('.section-sticky-cart');
    
    // Check hide_on_footer setting
    const hideOnFooter = stickyCartWrapper?.dataset?.hideOnFooter === 'true';

    if (stickyCartWrapper && stickyCartWrapper.dataset && stickyCartWrapper.dataset.triggerOffset) {
        const offsetString = stickyCartWrapper.dataset.triggerOffset;
        const parsedOffset = parseInt(offsetString, 10);
        if (!isNaN(parsedOffset)) {
            triggerOffsetValue = parsedOffset;
        }
    } else {
        // Fallback to global query if not found on wrapper
        const globalStickyCartSection = document.querySelector('.section-sticky-cart');
        if (globalStickyCartSection && globalStickyCartSection.dataset && globalStickyCartSection.dataset.triggerOffset) {
            const offsetString = globalStickyCartSection.dataset.triggerOffset;
            const parsedOffset = parseInt(offsetString, 10);
            if (!isNaN(parsedOffset)) {
                triggerOffsetValue = parsedOffset;
            }
        }
    }
    
    this.mainTriggerOffset = `${triggerOffsetValue}%`; // Used for debug display
    
    // Get footer offset value from data attribute
    const footerOffsetString = stickyCartWrapper?.dataset?.footerOffset || '50';
    this.footerPixelOffset = parseInt(footerOffsetString, 10);

    this.lastFooterActive = false; // Initialize footer state

    this.refreshCallCount = 0;
    this.lastRefreshTime = Date.now();

    // Main element detection using IntersectionObserver
    this.mainElementScrolledPast = false;
    this.triggerLineHasPassedElement = false;
    
    // Create IntersectionObserver for main add-to-cart button
    this.mainObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const wasScrolledPast = this.mainElementScrolledPast;
        
        const rect = entry.boundingClientRect;
        const triggerLine = window.innerHeight * (triggerOffsetValue / 100);
        
        // Check if trigger line has passed the element (one-time check)
        if (!this.triggerLineHasPassedElement && rect.top < triggerLine) {
          this.triggerLineHasPassedElement = true;
        }
        
        // Three conditions must be met:
        // 1. Trigger line must have passed the element at some point
        // 2. Element must be out of view (not intersecting)
        // 3. Element must be above the viewport (scrolled past), not below it
        const elementIsAboveViewport = rect.bottom < 0;
        this.mainElementScrolledPast = this.triggerLineHasPassedElement && !entry.isIntersecting && elementIsAboveViewport;
        
        if (wasScrolledPast !== this.mainElementScrolledPast) {
          this.updateStickyState();
        }
      });
    }, {
      threshold: 0,
      rootMargin: '10px'
    });
    
    // Start observing the main element
    this.mainObserver.observe(mainTriggerElement);
    
    // Check trigger line position on scroll
    const checkTriggerLinePassed = () => {
      if (!this.triggerLineHasPassedElement) {
        const rect = mainTriggerElement.getBoundingClientRect();
        const triggerLinePos = window.innerHeight * (triggerOffsetValue / 100);
        if (rect.top < triggerLinePos) {
          this.triggerLineHasPassedElement = true;
          this.updateStickyState();
        }
      }
    };
    
    window.addEventListener('scroll', checkTriggerLinePassed);
    this.checkTriggerLinePassed = checkTriggerLinePassed;

    // Footer detection using IntersectionObserver
    if (this.footer && hideOnFooter) {
      // Create IntersectionObserver for footer
      this.footerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const wasActive = this.lastFooterActive;
          this.lastFooterActive = entry.isIntersecting;
          
          if (wasActive !== this.lastFooterActive) {
            this.updateStickyState();
          }
        });
      }, {
        threshold: 0,
        rootMargin: `-${this.footerPixelOffset}px 0px 0px 0px`
      });
      
      // Start observing the footer
      this.footerObserver.observe(this.footer);
    }

    // No longer need interval refresh with IntersectionObserver

    // Initial state check after a short delay
    setTimeout(() => {
      this.updateStickyState();
    }, 150);
  }

  /**
   * Update sticky cart visibility based on trigger states
   */
  updateStickyState() {
    if (!this.stickyContainer || !this.nativeAddToCartButton) {
      return;
    }

    const footerIsActive = this.lastFooterActive; 
    const isAtTop = window.scrollY <= 10;

    if (isAtTop) {
      this.hideSticky();
    } else if (footerIsActive) {
      if (this.stickyContainer) this.stickyContainer.classList.add('sticky-cart--hidden-footer');
      this.hideSticky();
    } else {
      if (this.stickyContainer) this.stickyContainer.classList.remove('sticky-cart--hidden-footer');
      
      if (this.mainElementScrolledPast) {
        this.showSticky();
      } else {
        this.hideSticky();
      }
    }
  }

  /**
   * Show the sticky cart
   */
  showSticky() {
    // TOP-OF-PAGE OVERRIDE: Never show if at top of page
    if (window.scrollY <= 10) {
      // Ensure it's hidden if this condition is met, even if called from updateStickyState
      if (this.stickyContainer && this.stickyContainer.classList.contains('is-visible')) {
        this.hideSticky();
      }
      return;
    }
    
    // Only show if not hidden by footer (check class directly on stickyContainer)
    if (this.stickyContainer && !this.stickyContainer.classList.contains('sticky-cart--hidden-footer')) {
      this.stickyContainer.classList.add('is-visible');
      this.stickyContainer.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Hide the sticky cart
   */
  hideSticky() {
    if (!this.stickyContainer) {
        return;
    }
    this.stickyContainer.classList.remove('is-visible');
    this.stickyContainer.setAttribute('aria-hidden', 'true');
  }

  /**
   * Set up event listeners
   */
  addEventListeners() {
    // Variant button clicks
    this.variantButtons?.forEach(button => {
      button.addEventListener('click', this.handleVariantButtonClick.bind(this));
    });

    // Add to cart button click
    this.addToCartButton?.addEventListener('click', this.handleAddToCartClick.bind(this));

    // Mobile variant trigger
    if (this.mobileVariantTrigger) {
      // Remove any existing listeners first
      this.mobileVariantTrigger.removeEventListener('click', this.toggleMobilePanel.bind(this));
      
      // Add new listener with specific binding
      const toggleHandler = (event) => {
        this.toggleMobilePanel(event);
      };
      
      this.mobileVariantTrigger.addEventListener('click', toggleHandler);
      
      // Store reference for cleanup
      this.mobileVariantTrigger._toggleHandler = toggleHandler;
    }

    // Mobile panel close button
    const closeButton = this.mobileVariantPanel?.querySelector('[data-close-variant-panel]');
    if (closeButton) {
      closeButton.addEventListener('click', this.closeMobilePanel.bind(this));
    }

    // Mobile panel variant selection
    const variantList = this.mobileVariantPanel?.querySelector('.sticky-cart__panel-variant-list');
    if (variantList) {
      variantList.addEventListener('click', this.handlePanelVariantClick.bind(this));
      variantList.addEventListener('keydown', this.handlePanelVariantKeydown.bind(this));
    }

    // Listen for cart events from other components using Horizon theme events
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartAddEvent.bind(this));
    
    // Listen for variant changes from main product form using Horizon theme events
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantChangeEvent.bind(this));
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    // Remove document-level listeners using correct event names
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartAddEvent.bind(this));
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantChangeEvent.bind(this));
    
    // Clean up mobile variant trigger listener
    if (this.mobileVariantTrigger && this.mobileVariantTrigger._toggleHandler) {
      this.mobileVariantTrigger.removeEventListener('click', this.mobileVariantTrigger._toggleHandler);
    }
  }

  /**
   * Handle variant button clicks
   */
  handleVariantButtonClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return; // Type guard
    const variantId = button.dataset.variantButton;
    const variantPrice = button.dataset.variantPrice;
    
    this.currentVariantId = variantId || null; // Ensure currentVariantId can be null
    this.updateNativeVariantSelector(variantId);
    this.updateStickyVariantButtons(button);
    this.updatePriceDisplay(variantPrice);
    this.updateProductImage(button);
    this.announceVariantChange(button.textContent?.trim() || '');
  }

  /**
   * Handle add to cart button clicks
   */
  async handleAddToCartClick(event) {
    event.preventDefault();
    
    if (!this.currentVariantId || !this.addToCartButton || !(this.addToCartButton instanceof HTMLButtonElement) ) return;
    
    // Check if button is disabled
    if (this.addToCartButton.disabled) return;
    
    // Store original button content if not already stored
    if (!this.originalButtonContent) {
      this.originalButtonContent = this.addToCartButton.innerHTML;
    }
    
    try {
      this.addToCartButton.disabled = true;
      const originalButtonText = this.addToCartButton.innerHTML;
      this.addToCartButton.innerHTML = '<span class="loading-spinner"></span> Adding...';

      // Create form data like Horizon's ProductFormComponent
      const formData = new FormData();
      formData.append('id', this.currentVariantId);
      formData.append('quantity', '1');
      
      // Add sections for cart items components like Horizon theme
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      // Ensure sections parameter is correctly formatted
      if (cartItemComponentsSectionIds.length > 0) {
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      }

      // Use Horizon's fetchConfig pattern for cart/add.js
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.root + 'cart/add.js', {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      
      const responseData = await response.json();
      
      if (responseData.status) {
        // Error occurred - dispatch CartErrorEvent like Horizon theme
        window.dispatchEvent(new CartErrorEvent(this.id || 'sticky-cart', responseData.message));
        throw new Error(responseData.message || 'Failed to add to cart');
      }
      
      // Success - dispatch CartAddEvent like Horizon theme
      this.dispatchEvent(new CartAddEvent({}, this.currentVariantId.toString(), {
        source: 'sticky-cart-component',
        itemCount: Number(formData.get('quantity')),
        productId: this.dataset.productId,
        sections: responseData.sections,
      }));
      
      // Success state
      this.addToCartButton.innerHTML = '<span class="success-icon">✓</span> Added!';
      
      const successTimeoutId = setTimeout(() => {
        if (this.addToCartButton) {
          this.addToCartButton.innerHTML = this.originalButtonContent || '';
          this.addToCartButton.disabled = false;
        }
      }, 2000);
      
    } catch (error) {
      // Dispatch error event using Horizon theme pattern
      window.dispatchEvent(new CartErrorEvent(this.id || 'sticky-cart', error.message || 'Failed to add to cart'));
      
      this.addToCartButton.innerHTML = 'Error - Try Again';
      
      const errorTimeoutId = setTimeout(() => {
        if (this.addToCartButton) {
          this.addToCartButton.innerHTML = this.originalButtonContent || 'Add to Cart';
          this.addToCartButton.disabled = false;
        }
      }, 2000);
    }
  }

  /**
   * Handle cart add events from other components
   */
  handleCartAddEvent(event) {
    // Update button state if needed
    if (this.addToCartButton && this.addToCartButton instanceof HTMLButtonElement && !this.addToCartButton.disabled) {
      const originalText = this.addToCartButton.innerHTML;
      this.addToCartButton.innerHTML = '<span class="success-icon">✓</span> Added!';
      setTimeout(() => {
        if (this.addToCartButton) {
          this.addToCartButton.innerHTML = originalText;
        }
      }, 1500);
    }
  }

  /**
   * Handle variant change events from main product form
   */
  handleVariantChangeEvent(event) {
    // Handle Horizon theme's VariantUpdateEvent structure
    const variant = event.detail.resource;
    if (variant && variant.id) {
      this.currentVariantId = variant.id.toString();
      this.updateStickyVariantButtons(); // Pass no arg to use this.currentVariantId
      
      // Update price if available
      if (variant.price) {
        this.updatePriceDisplay(variant.price.toString()); // Ensure price is string
      }
      
      // Update product image for external variant changes
      const correspondingButton = this.querySelector(`[data-variant-button="${variant.id}"]`);
      if (correspondingButton) {
        this.updateProductImage(correspondingButton);
      }
      
      // Update availability state
      if (this.addToCartButton && this.addToCartButton instanceof HTMLButtonElement) {
        const originalText = this.addToCartButton.dataset.originalText || 'Add to Cart';
        if (variant.available === false) {
          this.addToCartButton.disabled = true;
          this.addToCartButton.textContent = 'Sold Out';
        } else {
          this.addToCartButton.disabled = false;
          this.addToCartButton.textContent = originalText;
        }
      }
    }
  }

  /**
   * Update selected variant from DOM state
   */
  updateSelectedVariantFromDOM() {
    const initialVariantButton = this.querySelector('[data-variant-button][aria-checked="true"]');
    if (initialVariantButton instanceof HTMLElement && initialVariantButton.dataset.variantButton) {
      this.currentVariantId = initialVariantButton.dataset.variantButton;
      if (initialVariantButton.dataset.variantPrice) {
        this.updatePriceDisplay(initialVariantButton.dataset.variantPrice);
      }
    }
  }

  /**
   * Update native variant selector to sync with sticky cart
   */
  updateNativeVariantSelector(variantId) {
    if (!variantId) return;
    const nativeSelector = document.querySelector(`input[name="id"][value="${variantId}"]`);
    if (nativeSelector instanceof HTMLInputElement) {
      nativeSelector.checked = true;
      nativeSelector.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Update sticky variant button states
   */
  updateStickyVariantButtons(selectedButton = null) {
    this.variantButtons?.forEach(button => {
      if (!(button instanceof HTMLElement)) return; // Type guard
      const isSelected = selectedButton ? 
        (button === selectedButton) : 
        (button.dataset.variantButton === this.currentVariantId);
      button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
  }

  /**
   * Update price display elements
   */
  updatePriceDisplay(priceString) {
    if (typeof priceString !== 'string') return;
    this.priceElements?.forEach((element) => {
      element.textContent = priceString;
    });
  }

  /**
   * Update product image for selected variant
   */
  updateProductImage(variantButton) {
    if (!this.productImage) return;
    
    let imageUrl = null;
    let variantName = '';
    
    if (variantButton && variantButton.dataset) {
      // Get variant-specific image from button data
      imageUrl = variantButton.dataset.variantImage;
      variantName = variantButton.dataset.variantName || '';
    }
    
    // Fallback to product featured image if no variant image
    if (!imageUrl || imageUrl === 'null' || imageUrl === '') {
      imageUrl = this.dataset.productFeaturedImage;
    }
    
    if (imageUrl && imageUrl !== 'null' && imageUrl !== '') {
      // Add loading state
      this.productImage.style.opacity = '0.5';
      
      // Update image source
      this.productImage.src = imageUrl;
      this.productImage.alt = variantName || this.dataset.productTitle || '';
      
      // Restore opacity when loaded
      this.productImage.onload = () => {
        this.productImage.style.opacity = '1';
      };
      
      // Handle load errors
      this.productImage.onerror = () => {
        // Fallback to product featured image on error
        if (this.dataset.productFeaturedImage && imageUrl !== this.dataset.productFeaturedImage) {
          this.productImage.src = this.dataset.productFeaturedImage;
          this.productImage.alt = this.dataset.productTitle || '';
        }
        this.productImage.style.opacity = '1';
      };
    }
  }

  /**
   * Announce variant change for accessibility
   */
  announceVariantChange(variantText) {
    if (typeof variantText !== 'string' || !document.body) return;
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `Selected variant: ${variantText}`;
    
    document.body.appendChild(announcement);
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Toggle mobile variant panel
   */
  toggleMobilePanel(event) {
    // Prevent event bubbling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Debouncing - prevent rapid toggles
    if (this.mobilePanel.isToggling) {
      return;
    }
    
    if (this.mobilePanel.isOpen) {
      this.closeMobilePanel();
    } else {
      this.openMobilePanel();
    }
  }

  /**
   * Open mobile variant panel
   */
  openMobilePanel() {
    if (!this.mobileVariantPanel) {
      return;
    }
    
    // Set toggling flag
    this.mobilePanel.isToggling = true;
    
    this.mobilePanel.isOpen = true;
    this.mobileVariantPanel.classList.add('is-open');
    this.mobileVariantPanel.setAttribute('aria-hidden', 'false');
    this.mobileVariantTrigger?.setAttribute('aria-expanded', 'true');
    
    // Set up focus trapping
    this.setupFocusTrapping();
    
    // Focus first focusable element
    if (this.mobilePanel.firstFocusableElement && this.mobilePanel.firstFocusableElement instanceof HTMLElement) {
      this.mobilePanel.firstFocusableElement.focus();
    }
    
    // Add keydown listener for ESC key
    document.addEventListener('keydown', this.handlePanelKeydown.bind(this));
    
    // Clear toggling flag after transition
    setTimeout(() => {
      this.mobilePanel.isToggling = false;
    }, 350);
  }

  /**
   * Close mobile variant panel
   */
  closeMobilePanel() {
    if (!this.mobileVariantPanel) return;
    
    // Set toggling flag
    this.mobilePanel.isToggling = true;
    
    this.mobilePanel.isOpen = false;
    this.mobileVariantPanel.classList.remove('is-open');
    this.mobileVariantPanel.setAttribute('aria-hidden', 'true');
    this.mobileVariantTrigger?.setAttribute('aria-expanded', 'false');
    
    // Return focus to trigger
    if (this.mobileVariantTrigger instanceof HTMLElement) {
      this.mobileVariantTrigger.focus();
    }
    
    // Remove keydown listener
    document.removeEventListener('keydown', this.handlePanelKeydown.bind(this));
    
    // Clear toggling flag after transition
    setTimeout(() => {
      this.mobilePanel.isToggling = false;
    }, 350);
  }

  /**
   * Set up focus trapping for mobile panel
   */
  setupFocusTrapping() {
    const focusableElements = this.mobileVariantPanel?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    this.mobilePanel.focusableElements = focusableElements ? Array.from(focusableElements) : [];
    this.mobilePanel.firstFocusableElement = this.mobilePanel.focusableElements[0] || null;
    this.mobilePanel.lastFocusableElement = this.mobilePanel.focusableElements.length > 0 ? 
                                            this.mobilePanel.focusableElements[this.mobilePanel.focusableElements.length - 1] : 
                                            null;
  }

  /**
   * Handle keydown events in mobile panel
   */
  handlePanelKeydown(event) {
    if (event.key === 'Escape') {
      this.closeMobilePanel();
      return;
    }
    
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === this.mobilePanel.firstFocusableElement) {
          event.preventDefault();
          if (this.mobilePanel.lastFocusableElement instanceof HTMLElement) this.mobilePanel.lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === this.mobilePanel.lastFocusableElement) {
          event.preventDefault();
          if (this.mobilePanel.firstFocusableElement instanceof HTMLElement) this.mobilePanel.firstFocusableElement.focus();
        }
      }
    }
  }

  /**
   * Handle mobile panel variant clicks
   */
  handlePanelVariantClick(event) {
    if (!event.target) return;
    const variantItem = (event.target instanceof Element) ? event.target.closest('.sticky-cart__panel-variant-item') : null;

    if (!(variantItem instanceof HTMLElement) || variantItem.getAttribute('aria-disabled') === 'true') return;
    
    const variantId = variantItem.dataset.variantId;
    const variantPrice = variantItem.dataset.variantPrice;
    
    // Extract only the variant name, not the price - FIX for mobile price display bug
    const variantTitle = (() => {
      // Primary: Extract from dedicated name element
      const nameElement = variantItem.querySelector('.sticky-cart__panel-variant-name');
      if (nameElement && nameElement.textContent?.trim()) {
        return nameElement.textContent.trim();
      }
      
      // Fallback 1: Try data attribute if available
      if (variantItem.dataset.variantTitle) {
        return variantItem.dataset.variantTitle;
      }
      
      // Fallback 2: Extract from desktop variant button (cross-reference)
      if (variantId) {
        const desktopButton = document.querySelector(`[data-variant-button="${variantId}"] .sticky-cart__variant-text`);
        if (desktopButton && desktopButton.textContent?.trim()) {
          return desktopButton.textContent.trim();
        }
      }
      
      // Last resort: Use full text (original buggy behavior)
      return variantItem.textContent?.trim() || '';
    })();
    
    this.currentVariantId = variantId || null;
    this.updateNativeVariantSelector(variantId);
    this.updateStickyVariantButtons();
    this.updatePriceDisplay(variantPrice);
    this.updateMobileTriggerText(variantTitle);
    this.updatePanelSelection(variantItem);
    this.announceVariantChange(variantTitle);
    
    // Close panel after selection
    setTimeout(() => this.closeMobilePanel(), 150);
  }

  /**
   * Handle mobile panel variant keydown
   */
  handlePanelVariantKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handlePanelVariantClick(event);
    }
  }

  /**
   * Update mobile trigger text
   */
  updateMobileTriggerText(variantTitle) {
    if (typeof variantTitle !== 'string') return;
    const titleElement = this.mobileVariantTrigger?.querySelector('[data-current-variant-title]');
    if (titleElement) {
      titleElement.textContent = variantTitle;
    }
  }

  /**
   * Update panel selection state
   */
  updatePanelSelection(selectedItem) {
    if (!(selectedItem instanceof HTMLElement)) return;
    const allItems = this.mobileVariantPanel?.querySelectorAll('.sticky-cart__panel-variant-item');
    allItems?.forEach(item => {
      if (item instanceof HTMLElement) { // Ensure item is an HTMLElement
        item.setAttribute('aria-checked', item === selectedItem ? 'true' : 'false');
      }
    });
  }

  /**
   * Setup resize handling (legacy approach)
   */
  setupResizeHandling() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
          ScrollTrigger.refresh();
        }
      }, 250);
    });
  }
}

// Register the custom element
if (!customElements.get('sticky-cart-component')) {
  customElements.define('sticky-cart-component', StickyCartComponent);
}

export default StickyCartComponent; 